import React, { useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent } from '@mui/material'
import { useForm } from 'react-hook-form'
import { Brand, PartialAction, Position, State } from 'src/types'
import { useLocale } from 'src/locales/useLocale'
import { SaveButton } from 'src/features/simulationConfirm/Components/SaveButton'
import { PriceArea } from 'src/features/simulationConfirm/Components/PriceArea'
import { ConsumerForm } from 'src/features/simulationConfirm/Components/ConsumerForm'
import { SavedIdDisplay } from 'src/features/simulationConfirm/Components/SavedIdDisplay'
import { SavedCaution } from 'src/features/simulationConfirm/Components/SavedCaution'
import { watchFormDefaultValue } from 'src/constants'
import { useCanStepFurther } from 'src/features/simulationConfirm/hooks/useCanStepFurther'
import { YsrCaution } from 'src/features/simulationConfirm/Components/YsrCaution'
import { UsModelCaution } from 'src/features/simulationConfirm/Components/UsModelCaution'
import { useRouter } from 'next/router'
import { SimulationContent } from 'src/features/simulationConfirm/Components/SimulationContents'
import { FigureBackOnConfirm } from 'src/Components/Domain/FigureBackOnConfirm'
import { FigureFrontOnConfirm } from 'src/Components/Domain/FigureFrontOnConfirm'

type Props = {
  state?: State
  brand: Brand
  position: Position
  isDialogOpen: boolean
  handleDialogClose: () => void
  dispatch: React.Dispatch<PartialAction>
}

export const SimulationConfirmDialog: React.FC<Props> = ({ state, brand, position, isDialogOpen, handleDialogClose, dispatch }) => {
  const { watch, register } = useForm(watchFormDefaultValue)
  const { t, language } = useLocale()
  const [savedId, setSavedId] = useState<string>('')
  const [isCopied, setCopied] = useState<boolean>(false)
  const { canSave, canInputConsumer, canClose, existArribaFontNoPointColor, existEmoji, setIsSave } = useCanStepFurther(
    state,
    brand,
    position,
    isCopied,
    t,
    language,
    watch
  )
  const router = useRouter()
  const paths = router.asPath.split('/')
  const isStaffOnly = paths.includes('staff-only')

  const handleClear = () => {
    handleDialogClose()
    setSavedId('')
    setIsSave(false)
    setCopied(false)
  }
  const props = { state: { ...state, isStaffOnly }, brand, position }
  const productNumber = state?.baseModel.productNumber

  return (
    <Dialog
      open={isDialogOpen}
      maxWidth={'xl'}
      sx={{
        '& .MuiDialog-paper': {
          margin: '16px'
        }
      }}
    >
      <DialogContent style={{ padding: '16px 16px' }}>
        <Box display={'flex'}>
          <Box>
            <FigureBackOnConfirm {...{ ...props }} domId={'canvasBackConfirm'} />
          </Box>
          <Box>
            <FigureFrontOnConfirm {...{ ...props }} domId={'canvasFrontConfirm'} />
          </Box>
        </Box>
        <Box px={1} textAlign="left" color={'red'} bgcolor={'white'} fontSize={14}>
          {t.DRAWING_CAUTION}
        </Box>
        <SimulationContent {...{ ...props }} shouldTranslate={false} />
        {isStaffOnly && <Box color="blue">{t.STAFF_ONLY}</Box>}
        <YsrCaution {...{ brand, productNumber }} />
        <PriceArea {...{ ...props }} />
        <ConsumerForm {...{ position, existEmoji, dispatch, register, existArribaFontNoPointColor }} isDisabled={!canInputConsumer} />
      </DialogContent>

      <DialogActions style={{ flex: 'unset', display: 'unset' }}>
        <SavedIdDisplay savedId={savedId} isCopied={isCopied} setCopied={setCopied} />
        <UsModelCaution {...{ productNumber }} />
        <Box display={'flex'} justifyContent={'space-around'}>
          <SaveButton {...{ ...props, setSavedId, setIsSave }} email={watch('mailAddress')} disabled={!canSave} />
          <Button variant="outlined" onClick={handleClear} disabled={!canClose}>
            {t.CLOSE}
          </Button>
        </Box>
        <SavedCaution {...{ savedId }} />
      </DialogActions>
    </Dialog>
  )
}
